import { Html5Qrcode } from 'html5-qrcode'
import { useCallback, useEffect, useId, useRef, useState } from 'react'

type BarcodeDetectorLike = {
  detect: (source: ImageBitmapSource) => Promise<Array<{ rawValue: string }>>
}

type BarcodeDetectorCtor = new (options?: { formats: string[] }) => BarcodeDetectorLike

type ScanEngine = 'barcode' | 'html5' | 'none'

type QrCameraScannerProps = {
  active: boolean
  onScan: (value: string) => void
  unsupportedMessage: string
  permissionDeniedMessage: string
  scanningHint: string
  inactiveMessage?: string
}

function detectEngine(): ScanEngine {
  if (typeof window === 'undefined') return 'none'
  if (!navigator.mediaDevices?.getUserMedia) return 'none'
  const barcode = (window as Window & { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector
  if (barcode) return 'barcode'
  return 'html5'
}

/** Kamera scan QR — mirror superoti-kitchen/src/shared/components/QrCameraScanner.tsx. */
export function QrCameraScanner({
  active,
  onScan,
  unsupportedMessage,
  permissionDeniedMessage,
  scanningHint,
  inactiveMessage,
}: QrCameraScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const html5Ref = useRef<Html5Qrcode | null>(null)
  const scanLockRef = useRef(false)
  const readerId = useId().replace(/:/g, '')
  const [error, setError] = useState<string | null>(null)
  const [isStarting, setIsStarting] = useState(false)
  const [engine] = useState<ScanEngine>(() => detectEngine())

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }, [])

  const stopHtml5 = useCallback(async () => {
    const scanner = html5Ref.current
    html5Ref.current = null
    if (!scanner) return
    try {
      if (scanner.isScanning) {
        await scanner.stop()
      }
      scanner.clear()
    } catch {
      // scanner sudah berhenti
    }
  }, [])

  useEffect(() => {
    if (!active) {
      stopCamera()
      void stopHtml5()
      return undefined
    }

    if (engine === 'none') {
      setError(unsupportedMessage)
      return undefined
    }

    let cancelled = false
    scanLockRef.current = false

    async function startBarcode() {
      const detectorCtor = (window as Window & { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector
      if (!detectorCtor) return

      setIsStarting(true)
      setError(null)
      const detector = new detectorCtor({ formats: ['qr_code'] })
      let raf = 0

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        })
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        streamRef.current = stream
        const video = videoRef.current
        if (video) {
          video.srcObject = stream
          try {
            await video.play()
          } catch (err) {
            if ((err as Error)?.name !== 'AbortError') throw err
          }
        }

        const tick = async () => {
          if (cancelled || scanLockRef.current) return
          const el = videoRef.current
          if (el && el.readyState >= 2) {
            try {
              const codes = await detector.detect(el)
              if (codes.length > 0 && codes[0].rawValue) {
                scanLockRef.current = true
                onScan(codes[0].rawValue.trim())
                return
              }
            } catch {
              // frame gagal — lanjut scan
            }
          }
          raf = requestAnimationFrame(() => void tick())
        }
        raf = requestAnimationFrame(() => void tick())
      } catch {
        if (!cancelled) setError(permissionDeniedMessage)
      } finally {
        if (!cancelled) setIsStarting(false)
      }

      return () => {
        cancelled = true
        cancelAnimationFrame(raf)
        stopCamera()
      }
    }

    async function startHtml5() {
      setIsStarting(true)
      setError(null)

      try {
        const scanner = new Html5Qrcode(readerId, { verbose: false })
        html5Ref.current = scanner
        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 10,
            qrbox: { width: 220, height: 220 },
            aspectRatio: 1.333,
          },
          (decodedText) => {
            if (cancelled || scanLockRef.current) return
            scanLockRef.current = true
            onScan(decodedText.trim())
          },
          () => {},
        )
      } catch {
        if (!cancelled) setError(permissionDeniedMessage)
      } finally {
        if (!cancelled) setIsStarting(false)
      }
    }

    let cleanup: (() => void) | undefined

    if (engine === 'barcode') {
      void startBarcode().then((fn) => {
        cleanup = fn
      })
    } else {
      void startHtml5()
    }

    return () => {
      cancelled = true
      cleanup?.()
      stopCamera()
      void stopHtml5()
    }
  }, [active, engine, onScan, permissionDeniedMessage, readerId, stopCamera, stopHtml5, unsupportedMessage])

  if (!active) {
    return (
      <div className="rounded-xl border-2 border-primary/30 bg-inverse-surface overflow-hidden aspect-[4/3] relative flex items-center justify-center">
        <div className="text-center px-6 space-y-3">
          <span className="material-symbols-outlined text-5xl text-white/70">qr_code_scanner</span>
          <p className="text-body-sm text-white/90">{inactiveMessage ?? scanningHint}</p>
        </div>
      </div>
    )
  }

  const statusText = isStarting ? scanningHint : error ?? scanningHint

  return (
    <div className="rounded-xl border-2 border-primary/30 bg-inverse-surface overflow-hidden aspect-[4/3] relative">
      {engine === 'barcode' && (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          playsInline
          muted
          aria-label="Kamera scan QR"
        />
      )}
      {engine === 'html5' && (
        <div id={readerId} className="absolute inset-0 [&_video]:object-cover [&_video]:w-full [&_video]:h-full" />
      )}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center p-6">
        <div className="w-full max-w-[220px] aspect-square border-2 border-white/80 rounded-2xl shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-black/55 px-3 py-2 z-10">
        <p className="text-body-sm text-white text-center">{statusText}</p>
      </div>
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-inverse-surface/90 p-4 z-20">
          <p className="text-body-sm text-white/90 text-center">{error}</p>
        </div>
      )}
    </div>
  )
}
